import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Phone, MapPin, Clock, Flame, Thermometer, Snowflake,
  Edit3, MoreVertical, MessageSquare, PhoneCall, Eye, RefreshCw
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import LeadEditDrawer from './LeadEditDrawer';

const PRIORITY_CONFIG = {
  hot: { icon: Flame, color: 'text-rose-500', bg: 'bg-rose-50', border: 'border-rose-200', ring: 'ring-1 ring-rose-300', bar: 'bg-rose-400', badge: 'bg-rose-100 text-rose-700' },
  warm: { icon: Thermometer, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', ring: '', bar: 'bg-amber-400', badge: 'bg-amber-100 text-amber-700' },
  cold: { icon: Snowflake, color: 'text-sky-500', bg: 'bg-sky-50', border: 'border-sky-200', ring: '', bar: 'bg-sky-400', badge: 'bg-sky-100 text-sky-700' },
};

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}
function getAvatarColor(name) {
  const colors = ['bg-violet-500', 'bg-indigo-500', 'bg-sky-500', 'bg-teal-500', 'bg-emerald-500', 'bg-amber-500', 'bg-orange-500', 'bg-rose-500'];
  return colors[(name || '').charCodeAt(0) % colors.length];
}
function formatFollowUp(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((d - now) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff === -1) return 'Yesterday';
    if (diff < 0) return `${Math.abs(diff)}d overdue`;
    return `in ${diff}d`;
  } catch { return null; }
}
function isOverdue(iso) {
  if (!iso) return false;
  try { return new Date(iso) < new Date(); } catch { return false; }
}

// ─── Score Ring ───────────────────────────────────────────────────────────────
function ScoreRing({ score }) {
  const r = 10;
  const circ = 2 * Math.PI * r;
  const filled = (score / 100) * circ;
  const color = score >= 70 ? '#10b981' : score >= 40 ? '#f59e0b' : '#94a3b8';
  return (
    <div className="flex flex-col items-center" title={`Lead score: ${score}/100`}>
      <svg width="28" height="28" className="-rotate-90">
        <circle cx="14" cy="14" r={r} fill="none" stroke="#e2e8f0" strokeWidth="3" />
        <circle cx="14" cy="14" r={r} fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={`${filled} ${circ - filled}`} strokeLinecap="round" />
      </svg>
      <span className="text-[9px] font-bold -mt-[18px] rotate-0 text-slate-600" style={{ lineHeight: 1 }}>{score}</span>
    </div>
  );
}

// ─── Aging Badge ─────────────────────────────────────────────────────────────
function AgingDot({ lastContactedAt, createdAt }) {
  const ref = lastContactedAt || createdAt;
  if (!ref) return <span className="w-2 h-2 rounded-full bg-slate-200" title="Never contacted" />;
  const days = Math.floor((Date.now() - new Date(ref)) / 86400000);
  const cfg = days <= 3
    ? { cls: 'bg-emerald-400', tip: `Last contact ${days}d ago` }
    : days <= 7
    ? { cls: 'bg-amber-400', tip: `${days}d since last contact` }
    : { cls: 'bg-rose-500 animate-pulse', tip: `⚠ ${days}d since last contact` };
  return <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.cls}`} title={cfg.tip} />;
}

export default function LeadCard({ lead, onStageChange, onRefresh, isDragging, bulkMode, isSelected, onSelect }) {
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const pc = PRIORITY_CONFIG[lead.priority] || PRIORITY_CONFIG.warm;
  const PriorityIcon = pc.icon;
  const overdue = isOverdue(lead.follow_up_date);
  const followUpLabel = formatFollowUp(lead.follow_up_date);
  const score = lead.lead_score || 0;

  const handleCardClick = (e) => {
    if (e.target.closest('[data-no-nav]')) return;
    if (bulkMode) { onSelect && onSelect(lead.id); return; }
    navigate(`/sales/leads/${lead.id}`);
  };

  return (
    <>
      <div
        onClick={handleCardClick}
        className={`
          group bg-white rounded-xl border cursor-pointer select-none
          transition-all duration-200 hover:shadow-md hover:-translate-y-0.5
          ${pc.border} ${pc.ring}
          ${isDragging ? 'opacity-50 rotate-2 shadow-xl' : ''}
          ${isSelected ? 'ring-2 ring-indigo-500 ring-offset-1' : ''}
        `}
      >
        {/* Priority bar */}
        <div className={`h-1 rounded-t-xl w-full ${pc.bar}`} />

        <div className="p-3 space-y-2">
          {/* Header */}
          <div className="flex items-start gap-2">
            {/* Bulk checkbox OR avatar */}
            {bulkMode ? (
              <div data-no-nav
                className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 bg-white'
                }`}
                onClick={() => onSelect && onSelect(lead.id)}
              >
                {isSelected && <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>}
              </div>
            ) : (
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${getAvatarColor(lead.full_name)}`}>
                {getInitials(lead.full_name)}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-sm text-slate-800 truncate">{lead.full_name}</span>
                <PriorityIcon className={`w-3 h-3 ${pc.color} flex-shrink-0`} />
                {lead.is_duplicate && (
                  <Badge className="text-[9px] px-1 py-0 h-4 bg-amber-100 text-amber-700 border border-amber-200 flex-shrink-0">
                    ×{lead.enquiry_number || '2'}
                  </Badge>
                )}
              </div>
              {(lead.nationality || lead.country_of_interest) && (
                <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{[lead.nationality, lead.country_of_interest].filter(Boolean).join(' → ')}</span>
                </div>
              )}
            </div>

            {/* More menu */}
            {!bulkMode && (
              <div data-no-nav>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <MoreVertical className="w-3.5 h-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onClick={() => navigate(`/sales/leads/${lead.id}`)}>
                      <Eye className="w-3.5 h-3.5 mr-2" /> View Details
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setEditOpen(true)}>
                      <Edit3 className="w-3.5 h-3.5 mr-2" /> Quick Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(`/sales/leads/${lead.id}?tab=calls`)}>
                      <PhoneCall className="w-3.5 h-3.5 mr-2" /> Log Call
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/sales/leads/${lead.id}?tab=notes`)}>
                      <MessageSquare className="w-3.5 h-3.5 mr-2" /> Add Note
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          {/* Product interest */}
          {(lead.product_interest || lead.visa_type) && (
            <div className="text-xs text-slate-600 bg-slate-50 rounded-md px-2 py-1 truncate">
              {lead.product_interest || lead.visa_type}
            </div>
          )}

          {/* Duplicate last-status hint */}
          {lead.is_duplicate && lead.stage !== 'registered' && (
            <div className="flex items-center gap-1 text-xs bg-amber-50 text-amber-700 rounded-md px-2 py-1">
              <RefreshCw className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">Returning enquiry</span>
            </div>
          )}

          {/* Stats row */}
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {lead.quoted_price && (
              <span className="font-semibold text-teal-700">₹{Number(lead.quoted_price).toLocaleString()}</span>
            )}
            {lead.calls_count > 0 && (
              <span className="flex items-center gap-0.5"><PhoneCall className="w-3 h-3" />{lead.calls_count}</span>
            )}
            {lead.emails_sent_count > 0 && (
              <span className="flex items-center gap-0.5"><Mail className="w-3 h-3" />{lead.emails_sent_count}</span>
            )}
            <div className="ml-auto flex items-center gap-1.5">
              <AgingDot lastContactedAt={lead.last_contacted_at} createdAt={lead.created_at} />
              <ScoreRing score={score} />
            </div>
          </div>

          {/* Follow-up */}
          {followUpLabel && (
            <div className={`flex items-center gap-1 text-xs rounded-md px-2 py-1 ${
              overdue ? 'bg-rose-50 text-rose-600 animate-pulse' : 'bg-sky-50 text-sky-600'
            }`}>
              <Clock className="w-3 h-3 flex-shrink-0" />
              <span>{overdue ? 'OVERDUE: ' : ''}{followUpLabel}</span>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-1 border-t border-slate-100">
            <div className="text-xs text-slate-400 truncate">{lead.assigned_to_name || 'Unassigned'}</div>
            {!bulkMode && (
              <Button data-no-nav variant="ghost" size="sm"
                className="h-6 px-2 text-xs text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 gap-1"
                onClick={() => setEditOpen(true)}>
                <Edit3 className="w-3 h-3" /> Edit
              </Button>
            )}
          </div>
        </div>
      </div>

      {editOpen && (
        <LeadEditDrawer lead={lead} open={editOpen} onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); onRefresh?.(); }} />
      )}
    </>
  );
}
